import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery } from "@tanstack/react-query";

import { Card, CardContent, CardHeader, CardFooter } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import {
  MessagesSquare,
  Target,
  Users,
  Info,
  ArrowRight,
  Sparkles,
  FileText,
  CheckCircle2,
  AlertCircle,
  Lightbulb,
  Loader2,
  Search,
  BookText,
  Zap,
  Rocket,
  Hash,
} from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Add metadata interface
interface TopicMetadata {
  type?: string;
  audience?: string;
  [key: string]: any;
}

// Form schema for Reddit post generation
const formSchema = z.object({
  subreddit: z.string().min(1, "Subreddit is required").refine(
    (val) => {
      // Allow r/ prefix but don't require it
      const subredditName = val.replace(/^r\//, "");
      // Validate that the subreddit name follows Reddit's rules
      // Only allow letters, numbers, and underscores, 3-21 characters
      return /^[a-zA-Z0-9_]{3,21}$/.test(subredditName);
    },
    {
      message: "Invalid subreddit name. Use 3-21 letters, numbers, or underscores.",
    }
  ),
  topic: z.string().min(10, "Topic must be at least 10 characters long"),
  postType: z.string().min(1, "Post type is required"),
  hiddenGoal: z.string().min(1, "Hidden goal is required"),
  useExistingTopic: z.boolean().default(false),
});

const predefinedSubreddits = [
  "r/EntrepreneurRideAlong",
  "r/startups",
  "r/SaaS",
];

const postTypes = [
  "Tips & tricks",
  "Ask for feedback",
  "Share personal experience",
  "Behind the Scenes Build Updates",
  "Mini Case Studies / Milestone Celebrations",
  "Industry or Market Trend Discussion",
  "Lessons Learned from a Failure",
  "Community Poll",
  "Survey",
  "Launch Announcement",
  "Re-Launch Announcement",
  "Product Comparisons",
  "Alternatives",
  "Resource Sharing",
  "Check Out This New Feature",
];

const hiddenGoals = [
  "There's no hidden goal",
  "Market research",
  "Subtle plug for the product's revised or improved approach",
  "Gather feedback from your target audience",
  "Establishing credibility & authority",
  "Position the product as a top solution in the market",
  "Position itself as better than the competitors",
  "Get people to ask about the project",
  "Get people to buy the product",
  "Overcome Common Objections",
  "Crowdsource Feature Ideas",
  "Validate Pricing Strategies",
];

// Add props interface
interface RedditLaunchPageProps {}

const RedditLaunchPage = ({}: RedditLaunchPageProps) => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("predefined");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Add state for topic selection
  const [useExistingTopic, setUseExistingTopic] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      subreddit: predefinedSubreddits[0],
      topic: "",
      postType: postTypes[0],
      hiddenGoal: hiddenGoals[0],
      useExistingTopic: false,
    },
  });

  // Fetch existing topics from the content topics
  const { data: contentTopics = [] } = useQuery({
    queryKey: ['content-topics', projectId],
    queryFn: async () => {
      if (!projectId || !user?.id) {
        return [];
      }

      const { data, error } = await supabase
        .from('generated_content')
        .select('id, title, content, metadata')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .eq('platform', 'topics')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching topics:', error);
        throw error;
      }
      
      return data.map(item => {
        // Safe casting of metadata
        const metadata = item.metadata as TopicMetadata || {};
        
        return {
          id: item.id,
          title: item.content || item.title || 'Untitled Topic',
          type: metadata.type || 'educational',
          audience: metadata.audience || 'general',
        };
      });
    },
    enabled: !!projectId && !!user?.id
  });

  // Filter topics based on search term
  const filteredTopics = contentTopics.filter(topic => 
    !searchTerm || topic.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset validation errors when switching tabs
  useEffect(() => {
    if (activeTab === "predefined") {
      form.setValue("subreddit", predefinedSubreddits[0]);
      form.clearErrors("subreddit");
    } else {
      // Clear the field when switching to custom tab
      form.setValue("subreddit", "");
      form.clearErrors("subreddit");
    }
  }, [activeTab, form]);

  // Update the form when useExistingTopic changes
  useEffect(() => {
    form.setValue("useExistingTopic", useExistingTopic);
    
    // Reset topic field when switching modes
    if (!useExistingTopic) {
      form.setValue("topic", "");
    } else if (contentTopics.length > 0) {
      form.setValue("topic", contentTopics[0].id);
    } else {
      // If no topics available but toggle is on, show warning
      toast({
        title: "No topics available",
        description: "There are no content topics available. Create topics first or use custom input.",
        variant: "destructive",
      });
      // Switch back to custom input
      setUseExistingTopic(false);
    }
  }, [useExistingTopic, form, contentTopics, toast]);

  const handleSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      if (!projectId || !user?.id) {
        toast({
          title: "Error",
          description: "Missing project information",
          variant: "destructive",
        });
        return;
      }

      // Check if using existing topics but none available
      if (values.useExistingTopic && contentTopics.length === 0) {
        toast({
          title: "Error",
          description: "No content topics available. Please create topics in the Content Topics section first or use custom topic input.",
          variant: "destructive",
        });
        return;
      }

      // Validate that the selected topic exists if using existing topics
      if (values.useExistingTopic) {
        const topicExists = contentTopics.some(t => t.id === values.topic);
        if (!topicExists) {
          toast({
            title: "Error",
            description: "The selected topic does not exist or was deleted. Please select another topic.",
            variant: "destructive",
          });
          return;
        }
      }

      setIsGenerating(true);
      
      // Set generation start time in localStorage to ensure loader appears immediately
      try {
        // Generate a unique ID for this generation session
        const generationId = `gen_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        localStorage.setItem("redditGenerationStarted", Date.now().toString());
        localStorage.setItem("redditGenerationId", generationId);
      } catch (e) {
        // Silent fail for localStorage errors
      }
      
      // Navigate to results page immediately to show loader
      navigate(`/projects/${projectId}/reddit/content?reddit_generating=true`, {
        state: { scrollToTop: true }
      });

      try {
        // Strip 'r/' prefix from subreddit if present
        const cleanSubreddit = values.subreddit.replace(/^r\//, "");

        // Handle topic selection
        let topicContent = values.topic;
        
        // If using existing topic, find the selected topic from contentTopics
        if (values.useExistingTopic) {
          const selectedTopic = contentTopics.find(t => t.id === values.topic);
          topicContent = selectedTopic ? selectedTopic.title : values.topic;
        }

        // Prepare metadata for content generation
        const metadata = {
          userId: user.id,
          topic: values.postType,
          contentTopic: topicContent,
          subreddit: cleanSubreddit,
          hiddenGoal: values.hiddenGoal,
        };

        // Call edge function to generate content
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-reddit-post`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              projectId,
              metadata,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to generate content");
        }

        await response.json();
        
        // No need to navigate again - we're already on the results page
      } catch (error) {
        console.error("Error generating post:", error);
        toast({
          title: "Error",
          description: error instanceof Error 
            ? error.message 
            : "Failed to generate Reddit post. Please try again.",
          variant: "destructive",
        });
        
        // Update URL with error parameter directly - we're already on the results page
        window.history.replaceState(
          {}, 
          "", 
          `/projects/${projectId}/reddit/content?error=generation_failed`
        );
      }
    },
    [projectId, user, navigate, toast, contentTopics, supabase]
  );

  // Helper function to format subreddit display
  const formatSubredditDisplay = (subreddit: string) => {
    if (!subreddit) return "";
    return subreddit.startsWith("r/") ? subreddit : `r/${subreddit}`;
  };

  // Track if the form is valid for disabling the submit button
  const isFormValid = form.formState.isValid;
  const hasFormErrors = Object.keys(form.formState.errors).length > 0;
  
  // Combined loading state from both local generation
  const isProcessing = isGenerating;

  return (
    <div className="container py-6 max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header */}
        <motion.div 
          className="flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight gradient-text">
                Reddit Post Generator
              </h1>
              <p className="text-zinc-500 mt-1">
                Generate authentic content that resonates with Reddit communities
              </p>
            </div>
          </div>
        </motion.div>

        {/* Main Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-10"
              >
                <div className="space-y-8">
                  {/* Subreddit Selection */}
                  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-700">
                    {/* Section header with icon */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-zinc-800 dark:to-zinc-900 px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                        <Users className="h-4 w-4 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Target Community</h3>
                    </div>

                    <div className="p-6">
                      <Tabs
                        defaultValue="predefined"
                        value={activeTab}
                        onValueChange={setActiveTab}
                        className="w-full"
                      >
                        <TabsList className="mb-5 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-lg">
                          <TabsTrigger 
                            value="predefined" 
                            className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 rounded-md transition-all"
                          >
                            <Hash className="h-4 w-4 mr-2" />
                            Recommended Subreddits
                          </TabsTrigger>
                          <TabsTrigger 
                            value="custom" 
                            className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400 rounded-md transition-all"
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Custom Subreddit
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="predefined" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {predefinedSubreddits.map((subreddit) => (
                              <div
                                key={subreddit}
                                className={`relative p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                                  form.getValues().subreddit === subreddit
                                    ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700/50 shadow-sm"
                                    : "bg-white dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700 hover:border-blue-200 dark:hover:border-blue-700/40 hover:shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
                                }`}
                                onClick={() => {
                                  form.setValue("subreddit", subreddit);
                                  form.clearErrors("subreddit");
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  {form.getValues().subreddit === subreddit ? (
                                    <div className="h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center border border-blue-300 dark:border-blue-700/80">
                                      <CheckCircle2 className="h-4 w-4 text-blue-600" />
                                    </div>
                                  ) : (
                                    <div className="h-5 w-5 rounded-full border-2 border-zinc-300 dark:border-zinc-600"></div>
                                  )}
                                  <span
                                    className={
                                      form.getValues().subreddit === subreddit
                                        ? "font-medium text-blue-700 dark:text-blue-300"
                                        : "text-zinc-700 dark:text-zinc-300"
                                    }
                                  >
                                    {subreddit}
                                  </span>
                                </div>
                                {form.getValues().subreddit === subreddit && (
                                  <div className="absolute top-0 right-0 h-4 w-4 translate-x-1/3 -translate-y-1/3 bg-blue-600 rounded-full border-2 border-white dark:border-zinc-800"></div>
                                )}
                              </div>
                            ))}
                          </div>
                        </TabsContent>

                        <TabsContent value="custom" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
                          <FormField
                            control={form.control}
                            name="subreddit"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Enter Subreddit Name</FormLabel>
                                <div className="flex items-center gap-2">
                                  <div className="relative flex-1">
                                    <FormControl>
                                      <Input
                                        placeholder="productmanagement"
                                        {...field}
                                        className="bg-white dark:bg-zinc-800 pl-8 border-zinc-200 dark:border-zinc-700 focus-visible:ring-blue-500/50 focus-visible:border-blue-500 shadow-sm"
                                        onChange={(e) => {
                                          // Normalize input by removing any invalid characters
                                          const value = e.target.value.trim();
                                          field.onChange(value);
                                        }}
                                      />
                                    </FormControl>
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-medium">
                                      r/
                                    </span>
                                  </div>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="h-9 w-9 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                                        >
                                          <Info className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-xs bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 shadow-lg">
                                        <p className="text-xs">
                                          Enter the subreddit name with or without the "r/" prefix.
                                          Use 3-21 letters, numbers, or underscores.
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                                <FormMessage className="text-xs mt-1.5" />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      </Tabs>

                      <div className="mt-4 text-xs text-zinc-500 dark:text-zinc-400 flex items-start gap-1.5 bg-zinc-50 dark:bg-zinc-800/40 p-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700">
                        <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5" />
                        <span>Select a community that aligns with your content topic for better engagement and higher chances of success</span>
                      </div>
                    </div>
                  </div>

                  {/* Content Topic Selection */}
                  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-700">
                    {/* Section header with icon */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-zinc-800 dark:to-zinc-900 px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                        <BookText className="h-4 w-4 text-blue-600" />
                      </div>
                      <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Content Topic</h3>
                    </div>

                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-5">
                        <Switch 
                          id="use-existing-topic" 
                          checked={useExistingTopic}
                          onCheckedChange={(checked) => setUseExistingTopic(checked)}
                          className="data-[state=checked]:bg-blue-600 h-6 w-11"
                        />
                        <Label htmlFor="use-existing-topic" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Use existing content topic</Label>
                      </div>

                      {useExistingTopic ? (
                        <div className="space-y-5">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                            <Input
                              type="search"
                              placeholder="Search topics..."
                              className="pl-9 bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus-visible:ring-blue-500/50 focus-visible:border-blue-500 shadow-sm"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </div>

                          <FormField
                            control={form.control}
                            name="topic"
                            render={({ field }) => (
                              <FormItem>
                                <div className="max-h-60 overflow-y-auto border border-zinc-200/80 dark:border-zinc-700 rounded-md bg-zinc-50/80 dark:bg-zinc-800/50 shadow-inner">
                                  {filteredTopics.length > 0 ? (
                                    <div className="space-y-1 p-1">
                                      {filteredTopics.map((topic) => (
                                        <div
                                          key={topic.id}
                                          className={`p-3 rounded-md cursor-pointer transition-colors duration-200 ${
                                            field.value === topic.id
                                              ? "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 shadow-sm"
                                              : "bg-white dark:bg-zinc-800/80 border border-transparent hover:border-zinc-200 dark:hover:border-zinc-700/50 hover:shadow-sm hover:bg-zinc-50 dark:hover:bg-zinc-700/40"
                                          }`}
                                          onClick={() => field.onChange(topic.id)}
                                        >
                                          <div className="flex items-start">
                                            <div className="flex-1 min-w-0">
                                              <div className="font-medium truncate text-zinc-800 dark:text-zinc-200">{topic.title}</div>
                                              <div className="flex mt-1.5 gap-2">
                                                <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40">{topic.type}</Badge>
                                                <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/40">{topic.audience}</Badge>
                                              </div>
                                            </div>
                                            {field.value === topic.id && (
                                              <CheckCircle2 className="h-5 w-5 text-blue-600 ml-2 flex-shrink-0" />
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="p-8 flex flex-col items-center justify-center text-center">
                                      <div className="w-12 h-12 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                                        <BookText className="h-6 w-6 text-blue-400" />
                                      </div>
                                      <p className="text-zinc-500 dark:text-zinc-400 mb-4 font-medium">
                                        {searchTerm ? "No matching topics found" : "No content topics available"}
                                      </p>
                                      {!searchTerm && (
                                        <Button 
                                          size="sm" 
                                          variant="outline" 
                                          onClick={() => navigate(`/projects/${projectId}/topics`)}
                                          className="border-blue-200 dark:border-blue-800/40 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                        >
                                          Create Topics
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <FormMessage className="text-xs mt-1.5" />
                              </FormItem>
                            )}
                          />
                        </div>
                      ) : (
                        <FormField
                          control={form.control}
                          name="topic"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Topic Description</FormLabel>
                              <div className="space-y-2">
                                <FormControl>
                                  <Textarea
                                    placeholder="Describe your content topic in detail..."
                                    className="min-h-[120px] bg-white dark:bg-zinc-800 resize-y border-zinc-200 dark:border-zinc-700 focus-visible:ring-blue-500/50 focus-visible:border-blue-500 shadow-sm"
                                    {...field}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      // Force form validation on change
                                      form.trigger("topic");
                                    }}
                                  />
                                </FormControl>
                                <div className="flex justify-between text-xs text-zinc-500">
                                  <span>Min 10 characters</span>
                                  <span className={`${field.value.length < 10 ? 'text-red-500' : 'text-zinc-500'}`}>
                                    {field.value.length} characters
                                  </span>
                                </div>
                              </div>
                              <FormMessage className="text-xs mt-1.5" />
                            </FormItem>
                          )}
                        />
                      )}

                      <div className="mt-4 text-xs text-zinc-500 dark:text-zinc-400 flex items-start gap-1.5 bg-zinc-50 dark:bg-zinc-800/40 p-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700">
                        <Lightbulb className="h-3.5 w-3.5 text-blue-500 mt-0.5" />
                        <span>
                          {useExistingTopic
                            ? "Select a content topic to repurpose for Reddit to ensure consistency across your strategy"
                            : "The more specific your topic description, the better the results will be"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Post Type and Hidden Goal in a 2-column layout on desktop */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Post Type Selection */}
                    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl hover:border-green-300 dark:hover:border-green-700">
                      {/* Section header with icon */}
                      <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-zinc-800 dark:to-zinc-900 px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                          <FileText className="h-4 w-4 text-green-600" />
                        </div>
                        <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Post Type</h3>
                      </div>

                      <div className="p-6">
                        <FormField
                          control={form.control}
                          name="postType"
                          render={({ field }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus:ring-green-500/50 shadow-sm">
                                    <SelectValue placeholder="Select post type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 shadow-lg">
                                  {postTypes.map((type) => (
                                    <SelectItem key={type} value={type} className="focus:bg-green-50 dark:focus:bg-green-900/20">
                                      {type}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-xs mt-1.5" />
                            </FormItem>
                          )}
                        />

                        <div className="mt-4 text-xs text-zinc-500 dark:text-zinc-400 flex items-start gap-1.5 bg-zinc-50 dark:bg-zinc-800/40 p-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700">
                          <AlertCircle className="h-3.5 w-3.5 text-green-500 mt-0.5" />
                          <span>Different subreddits respond better to different types of content</span>
                        </div>
                      </div>
                    </div>

                    {/* Hidden Goal Selection */}
                    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 overflow-hidden bg-white dark:bg-zinc-900 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl hover:border-amber-300 dark:hover:border-amber-700">
                      {/* Section header with icon */}
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-zinc-800 dark:to-zinc-900 px-6 py-4 border-b border-zinc-200 dark:border-zinc-700 flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                          <Target className="h-4 w-4 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">Hidden Goal</h3>
                      </div>

                      <div className="p-6">
                        <FormField
                          control={form.control}
                          name="hiddenGoal"
                          render={({ field }) => (
                            <FormItem>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus:ring-amber-500/50 shadow-sm">
                                    <SelectValue placeholder="Select hidden goal" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 shadow-lg">
                                  {hiddenGoals.map((goal) => (
                                    <SelectItem key={goal} value={goal} className="focus:bg-amber-50 dark:focus:bg-amber-900/20">
                                      {goal}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage className="text-xs mt-1.5" />
                            </FormItem>
                          )}
                        />

                        <div className="mt-4 text-xs text-zinc-500 dark:text-zinc-400 flex items-start gap-1.5 bg-zinc-50 dark:bg-zinc-800/40 p-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700">
                          <AlertCircle className="h-3.5 w-3.5 text-amber-500 mt-0.5" />
                          <span>The AI will craft a post that doesn't come across as promotional</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 px-2">
                  <Button
                    type="submit"
                    className="w-full gap-2.5 bg-primary hover:bg-primary/90 h-12 text-base font-medium shadow-md rounded-lg transition-all duration-300 hover:shadow-lg hover:scale-[1.01] active:scale-[0.98]"
                    disabled={isProcessing || !isFormValid || hasFormErrors}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {isGenerating ? "Processing..." : "Generating..."}
                      </>
                    ) : !isFormValid || hasFormErrors ? (
                      <>
                        <AlertCircle className="h-5 w-5" />
                        Please fill all fields
                      </>
                    ) : (
                      <>
                        <Zap className="h-5 w-5" />
                        Generate Reddit Post
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex items-center justify-center"
        >
          <div className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400 max-w-md text-center bg-zinc-50 dark:bg-zinc-800/50 px-4 py-3 rounded-lg border border-zinc-200 dark:border-zinc-700">
            <Info className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <p>
              Reddit is a powerful platform for reaching engaged communities. For best
              results, tailor your posts to each specific community.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RedditLaunchPage; 